import type { GhanttTimelineScale } from './types';

/** Convierte `Date` o ISO/string en timestamp ms. */
export function ghanttParseTime(d: Date | string): number {
  if (d instanceof Date) return d.getTime();
  if (typeof d === 'string') {
    const s = d.trim();
    // Solo fecha calendario: medianoche **local** (evita el desfase UTC de `new Date('YYYY-MM-DD')`).
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]);
      const day = Number(m[3]);
      const t = new Date(y, mo - 1, day).getTime();
      return Number.isFinite(t) ? t : NaN;
    }
  }
  const t = new Date(d as string).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/** Inicio del día local (00:00) para un timestamp. */
export function ghanttStartOfLocalDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Inicio del mes local. */
export function ghanttStartOfLocalMonth(ms: number): number {
  const d = new Date(ms);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Inicio de la hora local (minutos y segundos a 0). */
export function ghanttStartOfLocalHour(ms: number): number {
  const d = new Date(ms);
  d.setMinutes(0, 0, 0);
  return d.getTime();
}

/** Suma meses calendario en hora local (mismo día del mes cuando existe). */
export function ghanttAddCalendarMonths(ms: number, delta: number): number {
  const d = new Date(ms);
  d.setMonth(d.getMonth() + delta);
  return d.getTime();
}

export interface GhanttExpandedDayRangeOptions {
  /** Incluye el mes completo **anterior** al mes que contiene `viewStart`. @default true */
  includeLeadingFullMonth?: boolean;
}

/**
 * Rango de eje diario para rejilla y barras: mes anterior completo + meses hasta fin del mes de `viewEnd`.
 * `endExclusiveMs` es el instante 00:00 del día siguiente al último día incluido (intervalo semiabierto).
 */
export function ghanttResolveExpandedDayRange(
  viewStart: Date | string,
  viewEnd: Date | string,
  options?: GhanttExpandedDayRangeOptions,
): { startMs: number; endExclusiveMs: number } {
  const vs = ghanttParseTime(viewStart);
  const ve = ghanttParseTime(viewEnd);
  if (!Number.isFinite(vs) || !Number.isFinite(ve) || ve <= vs) {
    return { startMs: NaN, endExclusiveMs: NaN };
  }
  const includeLeading = options?.includeLeadingFullMonth !== false;
  const monthStartOfViewStart = ghanttStartOfLocalMonth(vs);
  const startMs = includeLeading
    ? ghanttAddCalendarMonths(monthStartOfViewStart, -1)
    : monthStartOfViewStart;
  const endExclusiveMs = ghanttAddCalendarMonths(ghanttStartOfLocalMonth(ve), 1);
  return { startMs, endExclusiveMs };
}

export interface GhanttTimelineSlot {
  /** Inicio del intervalo del slot (ms). */
  startMs: number;
  /** Etiqueta breve para cabecera. */
  label: string;
}

/** Piso superior de la cabecera Gantt (p. ej. meses o años), alineado en ancho con los slots inferiores. */
export interface GhanttUpperSegment {
  /** Texto visible (mes y año, año, etc.). */
  label: string;
  /** Cuántos slots del piso inferior ocupa (misma suma que `slots.length`). */
  slotSpan: number;
}

/**
 * Agrupa slots consecutivos para el primer piso de la cabecera (alineado con el segundo piso).
 * - `day` / `week`: agrupa por mes–año del inicio de cada slot.
 * - `month`: agrupa por año.
 * - `year`: sin piso superior (solo años en el piso inferior).
 */
export function ghanttBuildUpperFloorSegments(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
): GhanttUpperSegment[] {
  if (slots.length === 0) return [];

  if (scale === 'year') {
    return [];
  }

  if (scale === 'hour') {
    const segments: GhanttUpperSegment[] = [];
    let prevKey = -1;
    const date = new Date();
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
    const weekdays = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

    for (let i = 0; i < slots.length; i++) {
      const ms = slots[i].startMs;
      date.setTime(ms);
      // Key: YYYYMMDD
      const key = date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate();

      if (key !== prevKey) {
        const label = `${weekdays[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
        segments.push({ label, slotSpan: 1 });
        prevKey = key;
      } else {
        segments[segments.length - 1].slotSpan += 1;
      }
    }
    return segments;
  }

  const segments: GhanttUpperSegment[] = [];
  let prevKey = -1;
  const date = new Date();
  const monthsLong = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  for (let i = 0; i < slots.length; i++) {
    const ms = slots[i].startMs;
    date.setTime(ms);
    const y = date.getFullYear();
    const m = date.getMonth();
    const key = scale === 'month' ? y : y * 100 + m;

    if (key !== prevKey) {
      const label = scale === 'month' ? String(y) : `${monthsLong[m]} de ${y}`;
      segments.push({ label, slotSpan: 1 });
      prevKey = key;
    } else {
      segments[segments.length - 1].slotSpan += 1;
    }
  }
  return segments;
}

const DAY_MS = 86400000;
const HOUR_MS = 3600000;

/**
 * Genera slots alineados a `scale` entre `viewStart` y `viewEnd` para rejilla y cabecera.
 * Con `scale: 'day'`, por defecto amplía a meses completos (ver `ghanttResolveExpandedDayRange`).
 */
export function ghanttBuildTimelineSlots(
  viewStart: Date | string,
  viewEnd: Date | string,
  scale: GhanttTimelineScale = 'day',
  options?: GhanttExpandedDayRangeOptions,
): GhanttTimelineSlot[] {
  const a = ghanttParseTime(viewStart);
  const b = ghanttParseTime(viewEnd);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return [];

  const slots: GhanttTimelineSlot[] = [];

  if (scale === 'hour') {
    let t = ghanttStartOfLocalHour(a);
    const date = new Date(t);
    while (t < b) {
      const h = date.getHours();
      // Optimización: construcción manual de string para evitar toLocaleTimeString
      const label = (h < 10 ? '0' : '') + h + ':00';
      slots.push({ startMs: t, label });
      t += HOUR_MS;
      date.setTime(t);
    }
    return slots;
  }

  if (scale === 'year') {
    const startY = new Date(ghanttStartOfLocalDay(a)).getFullYear();
    const endAnchor = b - 1;
    const endY = new Date(endAnchor).getFullYear();
    for (let y = startY; y <= endY; y++) {
      slots.push({
        startMs: new Date(y, 0, 1).getTime(),
        label: String(y),
      });
    }
    return slots;
  }

  if (scale === 'day') {
    const { startMs, endExclusiveMs } = ghanttResolveExpandedDayRange(viewStart, viewEnd, options);
    if (!Number.isFinite(startMs) || !Number.isFinite(endExclusiveMs) || endExclusiveMs <= startMs) {
      return [];
    }
    let t = ghanttStartOfLocalDay(startMs);
    const date = new Date(t);
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
    while (t < endExclusiveMs) {
      const d = date.getDate();
      const m = months[date.getMonth()];
      // Optimización: evitar toLocaleDateString
      const label = (d < 10 ? '0' : '') + d + ' ' + m;
      slots.push({ startMs: t, label });
      t += DAY_MS;
      date.setTime(t);
    }
    return slots;
  }

  if (scale === 'week') {
    let t = ghanttStartOfLocalDay(a);
    const end = b;
    const dateS = new Date();
    const dateE = new Date();
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
    while (t < end) {
      const weekEndAnchor = t + 7 * DAY_MS - DAY_MS;
      dateS.setTime(t);
      dateE.setTime(weekEndAnchor);
      
      const dS = dateS.getDate();
      const mS = months[dateS.getMonth()];
      const dE = dateE.getDate();
      const mE = months[dateE.getMonth()];
      
      const label = (dS < 10 ? '0' : '') + dS + ' ' + mS + ' — ' + (dE < 10 ? '0' : '') + dE + ' ' + mE;
      
      slots.push({ startMs: t, label });
      t += 7 * DAY_MS;
    }
    return slots;
  }

  let t = ghanttStartOfLocalMonth(a);
  const end = b;
  const date = new Date();
  const months = ['Ene.', 'Feb.', 'Mar.', 'Abr.', 'May.', 'Jun.', 'Jul.', 'Ago.', 'Sept.', 'Oct.', 'Nov.', 'Dic.'];
  while (t < end) {
    date.setTime(t);
    const m = months[date.getMonth()];
    const y = date.getFullYear();
    slots.push({
      startMs: t,
      label: m + ' ' + y,
    });
    // Avanzar al siguiente mes local
    date.setMonth(date.getMonth() + 1);
    t = date.getTime();
  }
  return slots;
}

/**
 * Posición horizontal de un intervalo [start,end] dentro del viewport [viewStart,viewEnd] en %.
 * Devuelve `null` si no hay solape.
 */
export function ghanttBarLayoutPercent(
  viewStartMs: number,
  viewEndMs: number,
  barStartMs: number,
  barEndMs: number,
): { leftPct: number; widthPct: number } | null {
  if (!(viewEndMs > viewStartMs)) return null;
  if (!(barEndMs > barStartMs)) return null;
  if (barEndMs <= viewStartMs || barStartMs >= viewEndMs) return null;
  const clipStart = Math.max(barStartMs, viewStartMs);
  const clipEnd = Math.min(barEndMs, viewEndMs);
  const range = viewEndMs - viewStartMs;
  const leftPct = ((clipStart - viewStartMs) / range) * 100;
  const widthPct = ((clipEnd - clipStart) / range) * 100;
  return { leftPct, widthPct: Math.max(widthPct, 0.35) };
}

/**
 * Intervalo temporal que cubren los slots generados (para posicionar barras alineadas a la rejilla).
 * `endExclusiveMs` es exclusivo.
 */
export function ghanttSlotsTimeExtent(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
): { startMs: number; endExclusiveMs: number } | null {
  if (slots.length === 0) return null;
  const first = slots[0].startMs;
  const last = slots[slots.length - 1].startMs;
  if (scale === 'hour') {
    return { startMs: first, endExclusiveMs: last + HOUR_MS };
  }
  if (scale === 'day') {
    return { startMs: first, endExclusiveMs: last + DAY_MS };
  }
  if (scale === 'week') {
    return { startMs: first, endExclusiveMs: last + 7 * DAY_MS };
  }
  if (scale === 'year') {
    const d = new Date(last);
    d.setFullYear(d.getFullYear() + 1);
    return { startMs: first, endExclusiveMs: d.getTime() };
  }
  const d = new Date(last);
  d.setMonth(d.getMonth() + 1);
  return { startMs: first, endExclusiveMs: d.getTime() };
}
